import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatHeureMur, formatDurationMinutes } from '@/lib/dates';
import Sparkline from '@/components/Sparkline';

type TodayStats = {
  kpis: {
    totalRdv: number;
    enCours: number;
    attentePieces: number;
    termines: number;
    occupationPct: number;
  };
  chargeByMecano: Array<{
    mecanicien: {
      id: string;
      prenom: string;
      nom: string;
      couleur: string;
      specialite: string;
    };
    occupationPct: number;
    rdvCount: number;
    timeline: Array<{
      id: string;
      debut: string;
      fin: string;
      nature: string;
      statut: string;
    }>;
  }>;
  alerts: Array<{
    type: 'retard';
    interventionId: string;
    nature: string;
    client: string;
    mecanicien: string;
    moto: string;
  }>;
  urgents: Array<{
    id: string;
    nature: string;
    mecanicien: string;
    client: string;
  }>;
};

type TrendsStats = {
  period: '7d' | '30d';
  days: number;
  kpis: {
    totalTraites: number;
    delaiMoyenJours: number;
    occupationPct: number;
    urgentsCount: number;
  };
  repartition: { rapide: number; mecanique: number; lavage: number };
  topInterventions: Array<{ nature: string; count: number; avgMinutes: number }>;
  daily: Array<{
    date: string;
    totalRdv: number;
    occupationPct: number;
    urgentsCount: number;
  }>;
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<'7d' | '30d'>('30d');

  const todayQuery = useQuery({
    queryKey: ['stats', 'today'],
    queryFn: () => api.get<TodayStats>('/stats/today'),
    refetchInterval: 30_000,
  });

  const trendsQuery = useQuery({
    queryKey: ['stats', 'trends', period],
    queryFn: () => api.get<TrendsStats>(`/stats/trends?period=${period}`),
    refetchInterval: 5 * 60_000,
  });

  return (
    <div className="p-6 space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-text-primary">Tableau de Bord</h1>
        <p className="text-xs text-text-muted font-mono mt-0.5">Vue d'ensemble · Casablanca atelier</p>
      </header>

      {/* AUJOURD'HUI */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-3">
          Aujourd'hui
        </h2>

        {todayQuery.isError ? (
          <div
            role="alert"
            className="py-6 px-4 rounded-md bg-status-annule-bg border border-status-annule-border text-status-annule-text text-sm"
          >
            <p className="font-semibold">Erreur de chargement des données du jour</p>
            <p className="mt-1 text-text-secondary">
              Les indicateurs ci-dessous ne sont pas disponibles (ce n'est pas une journée vide).
            </p>
            <button
              type="button"
              onClick={() => void todayQuery.refetch()}
              className="mt-3 px-3 h-8 text-xs font-medium rounded-md bg-ktm-orange text-text-on-orange"
            >
              Réessayer
            </button>
          </div>
        ) : todayQuery.isLoading || !todayQuery.data ? (
          <div className="text-text-muted text-sm">Chargement…</div>
        ) : (
          <>
            <div className="grid grid-cols-5 gap-3 mb-4">
              <Kpi label="RDV jour" value={todayQuery.data.kpis.totalRdv} />
              <Kpi label="En cours" value={todayQuery.data.kpis.enCours} accent="orange" />
              <button type="button" onClick={() => navigate('/attente-pieces')} className="text-left w-full cursor-pointer">
                <Kpi label="Attente pièces" value={todayQuery.data.kpis.attentePieces} accent="warn" />
              </button>
              <Kpi label="Terminés" value={todayQuery.data.kpis.termines} accent="success" />
              <Kpi
                label="Taux occupation"
                value={`${todayQuery.data.kpis.occupationPct}%`}
                accent="orange"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              {/* Charge par mécano (timeline visuelle) */}
              <div className="col-span-2 bg-surface-white border border-border-light rounded-lg p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-text-primary mb-3">
                  Charge par mécanicien
                </h3>
                <div className="space-y-3">
                  {todayQuery.data.chargeByMecano.map((c) => (
                    <MecanoTimeline key={c.mecanicien.id} charge={c} />
                  ))}
                </div>
              </div>

              {/* Alertes */}
              <div className="bg-surface-white border border-border-light rounded-lg p-4 space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-text-primary">
                  Alertes & blocages
                </h3>
                {todayQuery.data.alerts.length === 0 && todayQuery.data.urgents.length === 0 ? (
                  <p className="text-xs text-text-muted">Aucune alerte 🎉</p>
                ) : (
                  <>
                    {todayQuery.data.alerts.map((a) => (
                      <AlertItem
                        key={a.interventionId}
                        kind="retard"
                        title={`Retard — ${a.nature}`}
                        subtitle={`${a.client} · ${a.mecanicien}`}
                      />
                    ))}
                    {todayQuery.data.urgents.map((u) => (
                      <AlertItem
                        key={u.id}
                        kind="urgent"
                        title={`Urgent — ${u.nature}`}
                        subtitle={`${u.client} · ${u.mecanicien}`}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </section>

      {/* TENDANCES */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
            Tendances ({trendsQuery.data ? `${trendsQuery.data.days} derniers jours` : '…'})
          </h2>
          <div className="flex items-center gap-1 border border-border-light rounded-md p-0.5">
            <button
              type="button"
              onClick={() => setPeriod('7d')}
              className={`px-2.5 py-1 text-xs font-medium rounded-sm ${
                period === '7d'
                  ? 'bg-ktm-orange text-text-on-orange'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              7 j
            </button>
            <button
              type="button"
              onClick={() => setPeriod('30d')}
              className={`px-2.5 py-1 text-xs font-medium rounded-sm ${
                period === '30d'
                  ? 'bg-ktm-orange text-text-on-orange'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              30 j
            </button>
          </div>
        </div>

        {trendsQuery.isError ? (
          <div
            role="alert"
            className="py-6 px-4 rounded-md bg-status-annule-bg border border-status-annule-border text-status-annule-text text-sm"
          >
            <p className="font-semibold">Erreur de chargement des tendances</p>
            <button
              type="button"
              onClick={() => void trendsQuery.refetch()}
              className="mt-3 px-3 h-8 text-xs font-medium rounded-md bg-ktm-orange text-text-on-orange"
            >
              Réessayer
            </button>
          </div>
        ) : trendsQuery.isLoading || !trendsQuery.data ? (
          <div className="text-text-muted text-sm">Chargement…</div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-3 mb-4">
              <Kpi
                label="RDV traités"
                value={trendsQuery.data.kpis.totalTraites}
                sparkValues={trendsQuery.data.daily.map((d) => d.totalRdv)}
              />
              <Kpi
                label="Délai moyen"
                value={`${trendsQuery.data.kpis.delaiMoyenJours}j`}
              />
              <Kpi
                label="Occupation"
                value={`${trendsQuery.data.kpis.occupationPct}%`}
                sparkValues={trendsQuery.data.daily.map((d) => d.occupationPct)}
              />
              <Kpi
                label="Urgences"
                value={trendsQuery.data.kpis.urgentsCount}
                sparkValues={trendsQuery.data.daily.map((d) => d.urgentsCount)}
                sparkColor="text-status-annule-text"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-surface-white border border-border-light rounded-lg p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-text-primary mb-3">
                  Répartition par type
                </h3>
                <div className="space-y-2">
                  <BarRow
                    label="Service Rapide"
                    count={trendsQuery.data.repartition.rapide}
                    total={trendsQuery.data.kpis.totalTraites}
                  />
                  <BarRow
                    label="Mécanique"
                    count={trendsQuery.data.repartition.mecanique}
                    total={trendsQuery.data.kpis.totalTraites}
                  />
                  <BarRow
                    label="Lavage"
                    count={trendsQuery.data.repartition.lavage}
                    total={trendsQuery.data.kpis.totalTraites}
                  />
                </div>
              </div>

              <div className="col-span-2 bg-surface-white border border-border-light rounded-lg p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-text-primary mb-3">
                  Top interventions
                </h3>
                {trendsQuery.data.topInterventions.length === 0 ? (
                  <p className="text-xs text-text-muted">Pas encore de données</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-text-muted uppercase tracking-wider border-b border-border-light">
                        <th className="text-left py-2">Nature</th>
                        <th className="text-right py-2">Volume</th>
                        <th className="text-right py-2">Durée moy.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trendsQuery.data.topInterventions.map((t) => (
                        <tr key={t.nature} className="border-b border-surface-divider last:border-0">
                          <td className="py-2 text-text-primary">{t.nature}</td>
                          <td className="py-2 text-right font-mono">{t.count}</td>
                          <td className="py-2 text-right font-mono text-text-muted">
                            {formatDurationMinutes(t.avgMinutes)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function Kpi({
  label,
  value,
  accent,
  sparkValues,
  sparkColor,
}: {
  label: string;
  value: string | number;
  accent?: 'orange' | 'warn' | 'success';
  sparkValues?: number[];
  sparkColor?: string;
}) {
  const accentClass =
    accent === 'orange'
      ? 'text-ktm-orange'
      : accent === 'warn'
        ? 'text-status-attente-pieces-text'
        : accent === 'success'
          ? 'text-status-termine-text'
          : 'text-text-primary';
  return (
    <div className="bg-surface-white border border-border-light rounded-lg p-4">
      <div className="text-xs text-text-muted uppercase tracking-wider mb-1">{label}</div>
      <div className="flex items-end justify-between gap-2">
        <div className={`text-2xl font-bold font-mono ${accentClass}`}>{value}</div>
        {sparkValues && sparkValues.length > 0 ? (
          <Sparkline
            values={sparkValues}
            color={sparkColor ?? 'text-ktm-orange'}
            ariaLabel={`Tendance ${label}`}
          />
        ) : null}
      </div>
    </div>
  );
}

type MecanoCharge = TodayStats['chargeByMecano'][number];

function MecanoTimeline({ charge }: { charge: MecanoCharge }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span
            className="w-5 h-5 rounded-sm flex items-center justify-center text-text-on-orange font-bold text-xs"
            style={{ backgroundColor: charge.mecanicien.couleur }}
          >
            {charge.mecanicien.prenom.charAt(0)}
          </span>
          <span className="text-sm font-medium">
            {charge.mecanicien.prenom} {charge.mecanicien.nom}
          </span>
        </div>
        <div className="text-xs text-text-muted font-mono">
          {charge.rdvCount} RDV · {charge.occupationPct}%
        </div>
      </div>
      <div className="relative h-6 bg-surface-active rounded-sm overflow-hidden">
        {/* Plage continue 9-18h = 540 min, pause déjeuner libre */}
        {charge.timeline.map((t) => {
          const debut = new Date(t.debut);
          const fin = new Date(t.fin);
          const startMin = (debut.getUTCHours() - 9) * 60 + debut.getUTCMinutes();
          const lenMin = (fin.getTime() - debut.getTime()) / 60_000;
          const leftPct = Math.max(0, (startMin / 540) * 100);
          const widthPct = (lenMin / 540) * 100;
          return (
            <div
              key={t.id}
              className="absolute top-0 bottom-0 px-2 flex items-center text-xs font-mono text-text-on-orange overflow-hidden whitespace-nowrap"
              style={{
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                backgroundColor: charge.mecanicien.couleur,
              }}
              title={`${t.nature} · ${formatHeureMur(debut)}→${formatHeureMur(fin)}`}
            >
              {widthPct > 5 ? t.nature : ''}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AlertItem({
  kind,
  title,
  subtitle,
}: {
  kind: 'retard' | 'urgent';
  title: string;
  subtitle: string;
}) {
  const badgeClass =
    kind === 'retard'
      ? 'bg-status-attente-pieces-bg text-status-attente-pieces-text'
      : 'bg-status-annule-bg text-status-annule-text';
  const badgeLabel = kind === 'retard' ? 'Retard' : 'Urgent';
  return (
    <div className="text-sm border-l-3 pl-3 border-l-status-attente-pieces-border">
      <div className="flex items-center gap-2 mb-0.5">
        <span className={`text-xs px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wide ${badgeClass}`}>
          {badgeLabel}
        </span>
        <span className="text-text-primary font-medium">{title}</span>
      </div>
      <div className="text-xs text-text-muted">{subtitle}</div>
    </div>
  );
}

function BarRow({ label, count, total }: { label: string; count: number; total: number }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-text-secondary">{label}</span>
        <span className="font-mono text-text-muted">
          {count} · {pct}%
        </span>
      </div>
      <div className="h-1.5 bg-surface-active rounded-sm overflow-hidden">
        <div className="h-full bg-ktm-orange" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
